
import * as React from "react";
import * as S from "@styled";
import * as AppPath from "../../model/AppPath";
import { Link, useLocation } from "react-router-dom";
import SplitPane from "react-split-pane";
import { Utilities } from "shared/utilities";
import { PageLoadSpinner } from "../../../shared/components/PageLoadSpinner";
import { StyledContentContainer, StyledContextPanel, StyledContextPanelCloseButton, StyledContextPanelTitleContainer } from "processEditor/components/Modals/AssistantModal";
import MonacoEditor from "shared/monaco/components/MonacoEditor";
import { ParameterEditorMode } from "processEditor/components/ParameterInput";
import styled, { css } from "styled-components";
import { DocumentationEditor, ParsedContext, AiMarkdownResponse, IDocumentationInitialContext, IDocumentationContext } from "./DocumentationEditor";
import useUserTheme from "shared/styles/hooks/useUserTheme";
import { ElementType, IElement, IElementDictionary, ISubProcessData, ITaskData } from "processEditor/model/IProcessData";
import { HashMap } from "shared/model/HashMap";

export const ProcessDocumentationView: React.FC<ProcessDocumentationViewProps> = (props) => {

    const location = useLocation<DocumentationLocationState>();
    const [theme] = useUserTheme();
    const [isEditorOpen, setIsEditorOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);
    const [documentation, setDocumentation] = React.useState<string>("");

    const [contextPanelOpen, setContextPanelOpen] = React.useState(false);
    const [parsedContext, setParsedContext] = React.useState<ParsedContext>({});
    const [contextInputValue, setContextInputValue] = React.useState('');

    const isNewProcess = props.uniqueIdentifier == null;

    const title = props.isTemplate
        ? "Template"
        : props.isSubprocess
            ? "Subprocess"
            : "Process";
    const baseUrl = props.isTemplate
        ? "api/template-documentation"
        : "api/process-documentation";

    React.useEffect(() => {
        const fetchDocumentation = async () => {
            const response = await Utilities.getFetchWithoutResultParsing(`${baseUrl}/${props.uniqueIdentifier}/${props.majorVersion}/${props.minorVersion}`);
            try {
                const currentDocumentation = await Utilities.parseJsonResponse<IDocumentationResponse>(response);
                setDocumentation(currentDocumentation.documentation);
            } catch (error) {
                // This is fine, there is just no documentation
                if (response.status != 404) {
                    Utilities.toastError(error);
                }
            }
            setIsLoading(false);
        };
        if (!isNewProcess) {
            fetchDocumentation();
        } else {
            setIsLoading(false);
        }

    }, []);

    React.useEffect(() => {
        const elementParameters = cleanupElementParameters(props.initialContext.elementParameters, props.initialContext.tasks, props.initialContext.subprocesses);

        handleContextInputChange(JSON.stringify({
            elementParameters: elementParameters,
            bpmnXml: props.initialContext.bpmnXml
        } as IDocumentationContext, null, 4));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialContext]);


    function cleanupElementParameters(obj: IElementDictionary, tasks: HashMap<ITaskData>, subprocesses: HashMap<ISubProcessData>): IElementDictionary {
        const cleanedObj: IElementDictionary = {};

        for (const [key, value] of Object.entries(obj)) {
            if (value !== null && typeof value === 'object') {
                // Filter out nulls from the first level of each nested object
                cleanedObj[key] = Object.fromEntries(
                    Object.entries(value).filter(([_, v]) => v !== null)) as IElement;

                // map task names to selectedTypeId in elementparameters
                if (cleanedObj[key].type == ElementType.Task && cleanedObj[key].selectedTypeId != null) {
                    cleanedObj[key].selectedTypeId = tasks[cleanedObj[key].selectedTypeId!].displayName;
                }
                // map subprocess names to selectedTypeId in elementparameters
                if (cleanedObj[key].type == ElementType.CallActivity && cleanedObj[key].selectedTypeId != null) {
                    cleanedObj[key].selectedTypeId = subprocesses[cleanedObj[key].selectedTypeId!].name;
                }
            } else {
                cleanedObj[key] = value;
            }
        }

        return cleanedObj;
    }

    const handleEditorVisibility = () => {
        setIsEditorOpen(prev => !prev);
        setContextPanelOpen(false);
    }

    const handleToggleContextPanel = () => {
        setContextPanelOpen(prev => !prev);
    }

    const handleContextInputChange = (context: string) => {
        setContextInputValue(context);
        if (context === '') {
            setParsedContext({});
            return;
        }
        let nextParsedContext: ParsedContext;
        try {
            nextParsedContext = { context: JSON.parse(context) };

        } catch (e) {
            nextParsedContext = { error: e.message };
        }
        setParsedContext(nextParsedContext);
    };

    const handleEditorSaveClick = async (documentation: string) => {
        if (isNewProcess && props.onAcceptChangesClick) {
            props.onAcceptChangesClick(documentation);
            setDocumentation(documentation);
            handleEditorVisibility();
            return;
        }
        try {
            await Utilities.postJsonAndParseResponse(`${baseUrl}/${props.uniqueIdentifier}/${props.majorVersion}/${props.minorVersion}`, documentation)
            Utilities.toastSuccess("Documentation saved");
            setDocumentation(documentation);
            handleEditorVisibility();

            if (!location.state?.fromEditor) {
                const redirectToProcessListUrl = AppPath.getProcessListPathFromReferrer(props.processId)
                setTimeout(() => window.location.href = redirectToProcessListUrl, 1000);
            }

        } catch (error) {
            Utilities.toastError(error);
        }

    }


    const renderBackLink = () => {
        const fromEditor = location.state?.fromEditor;

        if (!fromEditor) {
            const redirectToProcessListUrl = AppPath.getProcessListPathFromReferrer(props.processId)
            return <S.BackButton as={"a"} href={redirectToProcessListUrl}>Back</S.BackButton>
        }

        return <S.BackButton as={Link} to={"."}>Back</S.BackButton>
    }

    if (isLoading) {
        return <PageLoadSpinner />;
    }

    return <StyledDocumentationContentContainer>
        {/* @ts-expect-error: ACC-8426: react-split-pane has not updated their types to include React.PropsWithChildren which is required by React 18 */}
        <SplitPane
            split="vertical"
            primary="second"
            style={{
                overflow: isEditorOpen ? "none" : "auto",
                display: isEditorOpen ? "flex" : "flow-root"
            }}
            minSize={contextPanelOpen ? 250 : 0}
            defaultSize={contextPanelOpen ? 501 : 0}
            maxSize={contextPanelOpen ? 1200 : 0}
            allowResize={contextPanelOpen}
        >
            <StyledIntetedContainer contextPanelOpen={contextPanelOpen} >
                <S.RelativePageHeaderContainer style={{ marginBottom: '0' }}>
                    <h3>{title} /</h3>
                    <S.FlexContainer style={{ alignItems: "baseline" }} justifySpaceBetween={true} >
                        <S.FlexContainer style={{ alignItems: "baseline" }} gap={10} asRow={true}>
                            <h1>{props.name || "Name"}</h1> <h3>{props.majorVersion}.{props.minorVersion}</h3>
                            <span>/ Documentation</span>
                        </S.FlexContainer>
                        {!isEditorOpen && renderBackLink()}
                    </S.FlexContainer>
                </S.RelativePageHeaderContainer>
                <S.FlexContainer asColumn={true} style={{ gap: "2rem", marginTop: "2rem", flex: 1 }}>
                    {!isEditorOpen
                        ? !documentation
                            ? <>
                                <span>{`This ${title} doesn't have documentation.`}</span>
                                <S.Button onClick={handleEditorVisibility} >Create documentation</S.Button>
                            </>
                            : <>
                                <AiMarkdownResponse message={documentation} />
                                <S.Button onClick={handleEditorVisibility}>Edit</S.Button>
                            </>
                        : <DocumentationEditor
                            documentation={documentation}
                            contextPanelOpen={contextPanelOpen}
                            parsedContext={parsedContext}
                            isNewProcess={isNewProcess}
                            onSaveClicked={handleEditorSaveClick}
                            onCancelClick={handleEditorVisibility}
                            onToggleContextPanel={handleToggleContextPanel}
                        />}
                </S.FlexContainer>
            </StyledIntetedContainer>
            {contextPanelOpen ?
                <StyledContextPanel style={{ flex: 1 }}>
                    <StyledContextPanelTitleContainer>
                        <h3>The data being sent outside of Frends</h3>

                        <StyledContextPanelCloseButton
                            title="Close context panel"
                            onClick={handleToggleContextPanel}
                            disabled={!!parsedContext.error}
                        >
                            <i className="ci-close_big" />
                        </StyledContextPanelCloseButton>
                    </StyledContextPanelTitleContainer>
                    {parsedContext.error && (
                        <S.ErrorContainer>
                            <span>{parsedContext.error}</span>
                        </S.ErrorContainer>
                    )}
                    <S.ExpressionEditorContainer style={{ flex: 1 }}>
                        <MonacoEditor
                            onInstantChange={handleContextInputChange}
                            value={contextInputValue}
                            height={"100%"}
                            theme={theme}
                            mode={ParameterEditorMode.json}
                            key="assistant-modal-context-editor"
                        />
                    </S.ExpressionEditorContainer>
                </StyledContextPanel>
                : <span></span>}
        </SplitPane>
    </StyledDocumentationContentContainer>
}

const StyledDocumentationContentContainer = styled(StyledContentContainer)`
    max-height: calc(100vh - 80px);    
    .Pane1, .Pane2  {
        display: flex;
    }
    // https://stackoverflow.com/a/71876526
    .monaco-editor { position: absolute !important; }
`;

const StyledIntetedContainer = styled(S.IndentedContainer) <{ contextPanelOpen: boolean }>`
  ${(props) => props.contextPanelOpen && css`
       margin-right: 50px;
       `
    }
    flex: 1;
    display: flex;
    flex-direction: column;
`;

interface ProcessDocumentationViewProps {
    name: string | null;
    uniqueIdentifier?: string;
    majorVersion: number;
    minorVersion: number;
    isSubprocess: boolean;
    isTemplate: boolean;
    processId: number;
    initialContext: IDocumentationInitialContext;
    onAcceptChangesClick?: (newDocumentation: string) => void;
}

interface IDocumentationResponse {
    documentation: string;
    modifier: string;
    modifiedUtc: string;
}

export interface DocumentationLocationState {
    fromEditor?: boolean;
}